import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, ShieldAlert, ShieldCheck, Clock,
         FileText, Building2, Truck, Home, Bell, Wifi, WifiOff } from "lucide-react";
import api, { API_BASE, formatErr } from "../lib/api";

const STATUS_BADGE = {
  REJECTED: { bg: "#7F1D1D", fg: "#FCA5A5", icon: "🔒", label: "BLOCKED" },
  IN_REVIEW: { bg: "#1E3A8A", fg: "#93C5FD", icon: "👁", label: "IN REVIEW" },
  PENDING: { bg: "#374151", fg: "#D1D5DB", icon: "⏳", label: "PENDING" },
  APPROVED: { bg: "#14532D", fg: "#86EFAC", icon: "✓", label: "APPROVED" },
};

const DOMAIN_ICON = {
  process_plant: FileText,
  infrastructure: Truck,
  real_estate: Home,
};

function Tile({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} style={{
      padding: "12px 18px", borderRadius: 8,
      background: `${accent}1f`,
      border: `1.5px solid ${accent}`, minWidth: 170,
    }}>
      <div style={{ fontSize: 11, textTransform: "uppercase",
                    letterSpacing: 0.6, color: accent, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent,
                    fontFamily: "JetBrains Mono" }}>
        {value}
      </div>
    </div>
  );
}

export default function ReviewerInbox() {
  const navigate = useNavigate();
  const [data, setData] = useState({ rows: [], summary: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [domainFilter, setDomainFilter] = useState("ALL");
  // SSE live-push state
  const [live, setLive] = useState(false);
  const [toasts, setToasts] = useState([]); // newest first, max 4
  const toastIdRef = useRef(0);

  const pushToast = (text, kind = "info") => {
    const id = ++toastIdRef.current;
    setToasts((t) => [{ id, text, kind }, ...t].slice(0, 4));
    setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, 6000);
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/reviewer/inbox?status_filter=${filter}`);
      setData(r.data);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // ── SSE live stream — pushes a new event whenever any reviewer in any
  // accessible project records an audit decision. We refresh the table
  // and surface a transient toast so reviewers see new triage items in
  // real-time without polling.
  useEffect(() => {
    const url = `${API_BASE}/reviewer/inbox/stream`;
    const es = new EventSource(url, { withCredentials: true });
    es.onopen = () => setLive(true);
    es.onerror = () => setLive(false);
    es.addEventListener("snapshot", (evt) => {
      try { JSON.parse(evt.data); } catch { /* ignore */ }
      setLive(true);
    });
    es.addEventListener("decision", (evt) => {
      try {
        const d = JSON.parse(evt.data);
        const decisionLabel = d.decision === "rejected" ? "🔒 rejected"
          : d.decision === "approved" ? "✓ approved" : "↺ reset";
        pushToast(
          `${d.code} step ${d.step_no} ${decisionLabel} by ${d.by || "—"}`,
          d.decision === "rejected" ? "error" : "info");
        // Re-load the inbox so counters + rows reflect the change.
        load();
      } catch { /* ignore */ }
    });
    return () => es.close();
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    let rows = data.rows || [];
    if (domainFilter !== "ALL") {
      rows = rows.filter((r) => r.domain === domainFilter);
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      rows = rows.filter((r) =>
        (r.project_name || "").toLowerCase().includes(s) ||
        (r.code || "").toLowerCase().includes(s) ||
        (r.name || "").toLowerCase().includes(s));
    }
    return rows;
  }, [data, domainFilter, search]);

  const domains = useMemo(() => {
    const s = new Set((data.rows || []).map((r) => r.domain).filter(Boolean));
    return ["ALL", ...Array.from(s).sort()];
  }, [data]);

  return (
    <div style={{ minHeight: "100vh", background: "#0B0F19", color: "#F3F4F6",
                  fontFamily: "Inter, system-ui, sans-serif" }}>
      <header style={{
        padding: "16px 22px", borderBottom: "1px solid #1F2937",
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <button
          data-testid="reviewer-inbox-back"
          onClick={() => navigate("/projects")}
          style={{
            background: "transparent", color: "#F3F4F6",
            border: "1px solid #374151", padding: "6px 12px",
            borderRadius: 6, cursor: "pointer", display: "inline-flex",
            alignItems: "center", gap: 6, fontSize: 12,
          }}
        >
          <ArrowLeft size={14} /> Projects
        </button>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700,
                       letterSpacing: 0.4 }}>
            Reviewer Inbox
          </h1>
          <span style={{ color: "#9CA3AF", fontSize: 12 }}>
            Cross-project audit triage
          </span>
        </div>
        <div style={{ flex: 1 }} />
        <div
          data-testid="reviewer-inbox-live-status"
          title={live ? "Live updates connected" : "Reconnecting…"}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "4px 10px", borderRadius: 999,
            border: `1px solid ${live ? "#16a34a" : "#6B7280"}`,
            color: live ? "#86EFAC" : "#9CA3AF", fontSize: 11,
            fontWeight: 600, letterSpacing: 0.4,
          }}
        >
          {live ? <Wifi size={11} /> : <WifiOff size={11} />}
          {live ? "LIVE" : "RECONNECTING"}
          <span style={{
            width: 6, height: 6, borderRadius: "50%",
            background: live ? "#16a34a" : "#6B7280",
            boxShadow: live ? "0 0 6px #16a34a" : "none",
            animation: live ? "pulse 1.6s infinite" : "none",
          }} />
        </div>
        <button
          data-testid="reviewer-inbox-refresh"
          onClick={load}
          style={{
            background: "#FFB000", color: "#0B0F19", border: "none",
            padding: "7px 14px", borderRadius: 6, fontWeight: 600,
            cursor: "pointer", display: "inline-flex", alignItems: "center",
            gap: 6, fontSize: 12,
          }}
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </header>

      <main style={{ padding: 22, maxWidth: 1500, margin: "0 auto" }}>
        {error && (
          <div data-testid="reviewer-inbox-error" style={{
            background: "#7F1D1D", color: "#FEE2E2",
            padding: "10px 14px", borderRadius: 6, marginBottom: 16,
            fontSize: 13,
          }}>{error}</div>
        )}

        {/* Summary tiles */}
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap",
                      marginBottom: 22 }}>
          <Tile testid="tile-blocked-total" label="🔒 Blocked"
                value={data.summary?.blocked ?? 0} accent="#dc2626" />
          <Tile testid="tile-pending-total" label="⏳ Pending Review"
                value={data.summary?.pending ?? 0} accent="#FFB000" />
          <Tile testid="tile-approved-total" label="✓ Approved"
                value={data.summary?.approved ?? 0} accent="#16a34a" />
        </div>

        {/* Filters */}
        <div style={{
          display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14,
          padding: 12, background: "#0F172A",
          border: "1px solid #1F2937", borderRadius: 8,
        }}>
          <div style={{ display: "inline-flex", borderRadius: 6,
                        overflow: "hidden", border: "1px solid #374151" }}>
            {[["pending", "Needs Action", ShieldAlert],
              ["blocked", "Blocked", ShieldAlert],
              ["all", "All", FileText]].map(([k, label, Icon]) => (
              <button
                key={k}
                data-testid={`reviewer-tab-${k}`}
                onClick={() => setFilter(k)}
                style={{
                  background: filter === k ? "#FFB000" : "transparent",
                  color: filter === k ? "#0B0F19" : "#F3F4F6",
                  border: "none", padding: "7px 14px", cursor: "pointer",
                  fontSize: 12, fontWeight: 600,
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
          <input
            data-testid="reviewer-inbox-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by project, code or deliverable name…"
            style={{
              flex: 1, minWidth: 220,
              background: "#0B1220", color: "#F3F4F6",
              border: "1px solid #374151", padding: "7px 10px",
              borderRadius: 6, fontSize: 12,
            }}
          />
          <select
            data-testid="reviewer-inbox-domain"
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            style={{
              background: "#0B1220", color: "#F3F4F6",
              border: "1px solid #374151", padding: "7px 10px",
              borderRadius: 6, fontSize: 12, minWidth: 160,
            }}
          >
            {domains.map((d) => (
              <option key={d} value={d}>
                {d === "ALL" ? "All domains" : d.replace("_", " ")}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div style={{
          background: "#0F172A", border: "1px solid #1F2937",
          borderRadius: 8, overflow: "hidden",
        }}>
          {loading ? (
            <div data-testid="reviewer-inbox-loading"
                 style={{ padding: 36, textAlign: "center", color: "#9CA3AF" }}>
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="reviewer-inbox-empty"
                 style={{ padding: 60, textAlign: "center", color: "#9CA3AF" }}>
              <ShieldCheck size={42} style={{ opacity: 0.4, marginBottom: 8 }} />
              <div style={{ fontSize: 14 }}>
                Nothing to review.{" "}
                {filter === "pending"
                  ? "Every audit step is signed off — go ship something!"
                  : "No items match this filter."}
              </div>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse",
                            fontSize: 12 }}
                   data-testid="reviewer-inbox-table">
              <thead>
                <tr style={{ background: "#111827",
                             borderBottom: "1px solid #1F2937" }}>
                  {["Status", "Project", "Code", "Deliverable", "Rev",
                    "Steps", "Last Action", ""].map((h) => (
                    <th key={h} style={{
                      padding: "10px 12px", textAlign: "left",
                      color: "#9CA3AF", textTransform: "uppercase",
                      fontSize: 10, letterSpacing: 0.5,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const badge = STATUS_BADGE[r.review_status]
                                || STATUS_BADGE.PENDING;
                  const DomainIcon = DOMAIN_ICON[r.domain] || Building2;
                  const lastAt = r.last_decision_at
                    ? new Date(r.last_decision_at).toLocaleString()
                    : "—";
                  return (
                    <tr
                      key={`${r.project_id}-${r.code}-${r.version}`}
                      data-testid={`reviewer-row-${r.code}`}
                      style={{ borderBottom: "1px solid #1F2937" }}
                    >
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{
                          background: badge.bg, color: badge.fg,
                          padding: "3px 9px", borderRadius: 999,
                          fontSize: 10, fontWeight: 700,
                          letterSpacing: 0.4,
                        }}>
                          {badge.icon} {badge.label}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center",
                                      gap: 6 }}>
                          <DomainIcon size={13} style={{ color: "#9CA3AF" }} />
                          <span style={{ fontWeight: 600 }}>
                            {r.project_name}
                          </span>
                        </div>
                        <div style={{ fontSize: 10, color: "#6B7280",
                                      fontFamily: "JetBrains Mono",
                                      marginTop: 2 }}>
                          {r.project_number}
                        </div>
                      </td>
                      <td style={{ padding: "10px 12px",
                                   fontFamily: "JetBrains Mono",
                                   color: "#FFB000" }}>
                        {r.code}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        {r.name}
                      </td>
                      <td style={{ padding: "10px 12px",
                                   fontFamily: "JetBrains Mono" }}>
                        v{r.version}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 11 }}>
                        <span style={{ color: "#86EFAC" }}>
                          ✓ {r.approved_steps}
                        </span>
                        {" · "}
                        <span style={{ color: "#FCA5A5" }}>
                          ✗ {r.rejected_steps}
                        </span>
                        {" · "}
                        <span style={{ color: "#9CA3AF" }}>
                          {r.pending_steps} pending
                        </span>
                        <div style={{ height: 4, marginTop: 4,
                                      background: "#1F2937", borderRadius: 2,
                                      overflow: "hidden" }}>
                          <div style={{
                            display: "flex", height: "100%",
                            width: "100%",
                          }}>
                            <div style={{
                              width: `${(r.approved_steps / r.total_steps) * 100}%`,
                              background: "#16a34a",
                            }} />
                            <div style={{
                              width: `${(r.rejected_steps / r.total_steps) * 100}%`,
                              background: "#dc2626",
                            }} />
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 11,
                                   color: "#9CA3AF" }}>
                        <Clock size={11} style={{ verticalAlign: -1 }} />{" "}
                        {lastAt}
                        {r.last_decision_by && (
                          <div style={{ fontSize: 10 }}>
                            by {r.last_decision_by}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <button
                          data-testid={`reviewer-open-${r.code}`}
                          onClick={() => navigate(
                            `/projects/${r.project_id}/documents`)}
                          style={{
                            background: "#FFB000", color: "#0B0F19",
                            border: "none", padding: "5px 10px",
                            borderRadius: 4, fontSize: 11, fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Review →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ marginTop: 12, fontSize: 11, color: "#6B7280" }}>
          Showing {filtered.length} of {data.total} item
          {data.total === 1 ? "" : "s"} · Filter:{" "}
          <strong style={{ color: "#9CA3AF" }}>{filter}</strong>
        </div>
      </main>

      {/* Live decision toasts */}
      <div
        data-testid="reviewer-inbox-toasts"
        style={{
          position: "fixed", bottom: 22, right: 22, zIndex: 70,
          display: "flex", flexDirection: "column", gap: 8,
          pointerEvents: "none",
        }}
      >
        {toasts.map((t) => (
          <div key={t.id} style={{
            background: t.kind === "error" ? "#7F1D1D" : "#0F172A",
            border: `1.5px solid ${t.kind === "error" ? "#dc2626" : "#FFB000"}`,
            color: "#F3F4F6", padding: "10px 14px", borderRadius: 8,
            fontSize: 12, minWidth: 280, maxWidth: 360,
            boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", gap: 8,
            animation: "slideInRight 0.25s ease-out",
          }}>
            <Bell size={13} style={{
              color: t.kind === "error" ? "#FCA5A5" : "#FFB000",
              flexShrink: 0,
            }} />
            <span>{t.text}</span>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes slideInRight {
          from { transform: translateX(40px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
